export type Pos = [x: number, y: number];

export type ColorId = string;

export type CellState = ColorId | null;

/**
 * Reserved grid sentinel for a wall (blocked, permanently-non-empty) cell.
 * Not a real color id (generated colors are always `c1`, `c2`, ...), so every
 * existing "is this cell open" check (`cell === null` for empty, `cell ===
 * color` for own path) already treats a walled cell as blocked without
 * needing its own branch — see solver.ts, validateMove.ts, isWin.ts.
 */
export const WALL: ColorId = '#wall';

export type GameStatus = 'playing' | 'won';

export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert';

export interface LevelPair {
  color: ColorId;
  a: Pos;
  b: Pos;
}

export interface Level {
  id: string;
  size: number;
  difficulty: Difficulty;
  pairs: LevelPair[];
  /** Cells no path may enter or pass through. Omitted/empty on ordinary levels. */
  walls?: Pos[];
  meta?: {
    author?: string;
    created?: string;
    minMoves?: number;
  };
}

export interface LevelPack {
  packId: string;
  version: number;
  title: { ru: string; en: string };
  levelIds: string[];
  levels: Level[];
}

export interface LevelManifestEntry {
  id: string;
  count: number;
  file: string;
  unlockAfter: string | null;
}

export interface LevelManifest {
  version: number;
  totalLevels: number;
  packs: LevelManifestEntry[];
}

export interface GameState {
  size: number;
  endpoints: Map<ColorId, [Pos, Pos]>;
  paths: Map<ColorId, Pos[]>;
  grid: CellState[][];
  /** Blocked cells, copied from the level once at creation; never changes during play. */
  walls: Pos[];
  /** Remaining "reveal"-kind hints for this level (nudge hints are unlimited and untracked). */
  hintRevealsRemaining: number;
  status: GameStatus;
}

export interface MoveResult {
  ok: boolean;
  reason?: string;
  won?: boolean;
}

/**
 * c6 was #EC4899 (hue ~330°) — only 30° from c1's red (hue 0°) on the color
 * wheel, the tightest gap in the whole palette, and read as near-identical
 * once diluted by a glow/white-core render (user report, 2026-09-16). Moved
 * to #EC46C0 (hue ~316°, same saturation/lightness as before so it stays
 * just as vivid) for ~44° of separation from both red and purple, and still
 * clears the WCAG AA contrast floor the gear theme's dark-background audit
 * checks (`gearAccessibility.ts`) — an earlier darker pick (#D6249B) read
 * more "magenta" but dropped contrast below 4.5:1 and failed that test.
 *
 * c9/c10 are new — `packages/game/src/solver.ts`'s color generator produces
 * up to `c{pairCount}`, and expert-01's 8×8/10-pair levels (e.g. e-150) hit
 * c9/c10, which had no entry here and fell back to a flat gray (`#888` in
 * CanvasRenderer) for two whole pairs. Placed in the two largest remaining
 * hue gaps (red-amber, blue-purple) and tuned bright enough to also clear
 * the same 4.5:1 contrast floor — a lower-saturation "brown" and "slate"
 * were tried first and looked nicely distinct on paper but were too dark to
 * read against the dark theme (contrast ~2.6-4.1).
 *
 * **2026-09-19 re-tune:** the first c9/c10 pick (`#DD6C3C` terracotta,
 * `#6C75E0` indigo) passed the contrast check but was still flagged live —
 * user screenshot of an expert 10-pair level: "два серых" (two [of the
 * dots look] gray). Root cause wasn't hue placement, it was saturation: at
 * HSL S=70%/65% they were the two least-saturated colors in the whole
 * palette (everything else sits at S=71-92%) — individually not gray, but
 * next to 8 vivid neighbors they read as the washed-out ones. Kept the same
 * hue family (still in the red-amber and blue-purple gaps) but pushed
 * saturation up to ~92% each, which is what actually fixes "looks gray"
 * (contrast margin was never the problem — both new values clear 5.1+).
 */
export const COLORS: Record<string, string> = {
  c1: '#EF4444',
  c2: '#3B82F6',
  c3: '#22C55E',
  c4: '#F59E0B',
  c5: '#A855F7',
  c6: '#EC46C0',
  c7: '#14B8A6',
  c8: '#84CC16',
  c9: '#F55B14',
  c10: '#7F76F9',
};

export function posKey(pos: Pos): string {
  return `${pos[0]},${pos[1]}`;
}

export function posEqual(a: Pos, b: Pos): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

export function isAdjacent(a: Pos, b: Pos): boolean {
  const dx = Math.abs(a[0] - b[0]);
  const dy = Math.abs(a[1] - b[1]);
  return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
}

export function isInBounds(pos: Pos, size: number): boolean {
  return pos[0] >= 0 && pos[0] < size && pos[1] >= 0 && pos[1] < size;
}

export function cloneGrid(grid: CellState[][]): CellState[][] {
  return grid.map((row) => [...row]);
}

export function clonePaths(paths: Map<ColorId, Pos[]>): Map<ColorId, Pos[]> {
  const result = new Map<ColorId, Pos[]>();
  for (const [color, path] of paths) {
    result.set(color, path.map((p) => [...p] as Pos));
  }
  return result;
}

export function cloneState(state: GameState): GameState {
  return {
    size: state.size,
    endpoints: new Map(state.endpoints),
    paths: clonePaths(state.paths),
    grid: cloneGrid(state.grid),
    walls: state.walls,
    hintRevealsRemaining: state.hintRevealsRemaining,
    status: state.status,
  };
}

export function createEmptyGrid(size: number): CellState[][] {
  return Array.from({ length: size }, () => Array<CellState>(size).fill(null));
}

export function rebuildGrid(
  size: number,
  paths: Map<ColorId, Pos[]>,
  walls: Pos[] = [],
): CellState[][] {
  const grid = createEmptyGrid(size);
  for (const [x, y] of walls) {
    grid[y][x] = WALL;
  }
  for (const [color, path] of paths) {
    for (const [x, y] of path) {
      grid[y][x] = color;
    }
  }
  return grid;
}
